"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
    theme: Theme;
    resolvedTheme: "light" | "dark";
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = "nectarai-theme";

function getSystemTheme(): "light" | "dark" {
    if (typeof window === "undefined") return "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: "light" | "dark") {
    document.documentElement.setAttribute("data-theme", resolved);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<Theme>("system");
    const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("dark");

    // Resolve the effective theme
    const resolve = useCallback(
        (t: Theme): "light" | "dark" => (t === "system" ? getSystemTheme() : t),
        []
    );

    // Read persisted preference on mount
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
        const initial = stored && ["light", "dark", "system"].includes(stored) ? stored : "system";
        setThemeState(initial);
        const r = resolve(initial);
        setResolvedTheme(r);
        applyTheme(r);
    }, [resolve]);

    // Listen for OS preference changes when in "system" mode
    useEffect(() => {
        const mql = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => {
            if (theme === "system") {
                const r = getSystemTheme();
                setResolvedTheme(r);
                applyTheme(r);
            }
        };
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, [theme]);

    const setTheme = useCallback(
        (t: Theme) => {
            setThemeState(t);
            localStorage.setItem(STORAGE_KEY, t);
            const r = resolve(t);
            setResolvedTheme(r);
            applyTheme(r);
        },
        [resolve]
    );

    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
    return ctx;
}
